import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { Artifact, ArtifactType } from '@/models'
import * as joint from '@joint/plus'
import { useArtifactCacheStore } from './artifactCacheStore'

/**
 * UI state for artifact type containers
 */
export interface ArtifactTypeUiState {
  collapsed: boolean
  maxArtifactCountToShow: number
  containerSearchArtifactCount?: number
}

/**
 * Menu state for artifact type container menu
 */
export interface ArtifactTypeMenuState {
  artifactType: ArtifactType
  position: { x: number; y: number }
  cellView: any // joint.dia.CellView
  button?: any // joint.elementTools.Button
}

/**
 * Linked artifact type info for popup
 */
export interface LinkedArtifactTypeInfo {
  artifactType: ArtifactType
  linkCount: number
}

/**
 * State for linked artifact types popup (shown when clicking dot area)
 */
export interface LinkedTypesPopupState {
  artifact: Artifact
  direction: 'incoming' | 'outgoing'
  linkedTypes: LinkedArtifactTypeInfo[]
  position: { x: number; y: number }
}

/**
 * Explorer UI Store
 *
 * Manages Explorer page UI state including:
 * - Selected artifact/snapshot
 * - Artifact type expansion state
 * - Search mode
 * - Highlight settings
 * - Loaded artifacts tracking
 *
 * Pure UI state without data caching.
 */
export const useExplorerUiStore = defineStore('explorerUiStore', () => {
  // State
  const selectedArtifactId = ref<string | undefined>(undefined)
  const selectedArtifactTypeId = ref<number | undefined>(undefined)
  const selectedSnapshotId = ref<string | undefined>(undefined)
  const fixedArtifactId = ref<string | null>(null)
  const highlightAllDirectLinks = ref<boolean>(false)
  const searchMode = ref<boolean>(false)
  const loadedArtifacts = ref<Artifact[]>([])
  const artifactTypeUiStates = ref<Map<number, ArtifactTypeUiState>>(new Map())

  // Properties panel state - replaces EventBus updatePropertiesArtifact/ArtifactType
  const propertiesPanelArtifact = ref<Artifact | undefined>(undefined)
  const propertiesPanelArtifactType = ref<ArtifactType | undefined>(undefined)

  // Search state - replaces EventBus searchedArtifacts
  const searchResults = ref<Artifact[]>([])

  // Artifact type container search trigger - replaces EventBus searchArtifactsForArtifactType
  const artifactTypeSearchTrigger = ref<
    | {
        query: string
        artifactType: ArtifactType
        searchMode: boolean
        timestamp: number
      }
    | undefined
  >(undefined)

  // Highlight trigger - replaces EventBus 'highlightAllDirectLinksOnSelectionSwitch'
  // Holds the element view that should be highlighted after toggle
  const highlightTrigger = ref<{ elementView: any; timestamp: number } | undefined>(undefined)

  // Container menu state - replaces EventBus openContainerMenuArtifactType
  const artifactTypeMenuState = ref<ArtifactTypeMenuState | undefined>(undefined)

  // Linked artifact IDs for the current selection — bridge to HTML artifact row components
  // Populated by cell-selection when an artifact is selected; cleared on deselect/reset
  const linkedArtifactIds = ref<Set<number>>(new Set())

  // HTML artifact list triggers - for scrollable container interaction
  const artifactClickTrigger = ref<{ artifact: Artifact; timestamp: number } | undefined>(undefined)
  const artifactDblClickTrigger = ref<{ artifact: Artifact; timestamp: number } | undefined>(
    undefined,
  )
  const loadMoreArtifactsTrigger = ref<
    { artifactType: ArtifactType; timestamp: number } | undefined
  >(undefined)

  // Linked types popup state - shown when clicking on dot area
  const linkedTypesPopupState = ref<LinkedTypesPopupState | undefined>(undefined)

  // Actions

  /**
   * Set selected artifact ID (clears artifact type selection - single selection)
   */
  function setSelectedArtifact(id: string | undefined): void {
    selectedArtifactId.value = id
    if (id !== undefined) {
      selectedArtifactTypeId.value = undefined
    }
  }

  /**
   * Set selected artifact type ID (clears artifact selection - single selection)
   */
  function setSelectedArtifactType(id: number | undefined): void {
    selectedArtifactTypeId.value = id
    if (id !== undefined) {
      selectedArtifactId.value = undefined
    }
  }

  /**
   * Set selected snapshot ID
   */
  function setSelectedSnapshot(id: string | undefined): void {
    selectedSnapshotId.value = id
  }

  /**
   * Toggle fixed artifact state (double-click to pin/unpin)
   */
  function toggleFixedArtifact(artifactId: string): void {
    if (fixedArtifactId.value === artifactId) {
      fixedArtifactId.value = null
    } else {
      fixedArtifactId.value = artifactId
    }
  }

  /**
   * Clear fixed artifact
   */
  function clearFixedArtifact(): void {
    fixedArtifactId.value = null
  }

  /**
   * Check if an artifact is fixed
   */
  function isArtifactFixed(artifactId: string): boolean {
    return fixedArtifactId.value === artifactId
  }

  /**
   * Toggle highlight all direct links mode
   */
  function toggleHighlightAllDirectLinks(): void {
    highlightAllDirectLinks.value = !highlightAllDirectLinks.value
  }

  /**
   * Set highlight all direct links mode
   */
  function setHighlightAllDirectLinks(enabled: boolean): void {
    highlightAllDirectLinks.value = enabled
  }

  /**
   * Set search mode
   */
  function setSearchMode(enabled: boolean): void {
    searchMode.value = enabled
  }

  /**
   * Show artifact in properties panel (calls setSelectedArtifact)
   * @deprecated Use setSelectedArtifact instead
   */
  function showArtifactProperties(artifact: Artifact | undefined): void {
    setSelectedArtifact(artifact ? String(artifact.id) : undefined)
  }

  /**
   * Show artifact type in properties panel (calls setSelectedArtifactType)
   * @deprecated Use setSelectedArtifactType instead
   */
  function showArtifactTypeProperties(artifactType: ArtifactType | undefined): void {
    setSelectedArtifactType(artifactType?.id)
  }

  /**
   * Set search results
   * Replaces EventBus 'searchedArtifacts' event
   */
  function setSearchResults(artifacts: Artifact[]): void {
    searchResults.value = artifacts
  }

  /**
   * Clear search results
   */
  function clearSearchResults(): void {
    searchResults.value = []
  }

  /**
   * Trigger artifact type container search
   * Replaces EventBus 'searchArtifactsForArtifactType' event
   */
  function triggerArtifactTypeSearch(
    query: string,
    artifactType: ArtifactType,
    searchMode: boolean,
  ): void {
    artifactTypeSearchTrigger.value = {
      query,
      artifactType,
      searchMode,
      timestamp: Date.now(),
    }
  }

  /**
   * Trigger highlight for an element view
   * Replaces EventBus 'highlightAllDirectLinksOnSelectionSwitch' event
   */
  function triggerHighlightForElement(elementView: any): void {
    toggleHighlightAllDirectLinks()
    highlightTrigger.value = { elementView, timestamp: Date.now() }
  }

  /**
   * Open artifact type container menu
   * Replaces EventBus 'openContainerMenuArtifactType' event
   */
  function openArtifactTypeMenu(
    artifactType: ArtifactType,
    position: { x: number; y: number },
    cellView: joint.dia.CellView,
    button?: joint.elementTools.Button,
  ): void {
    artifactTypeMenuState.value = { artifactType, position, cellView, button }
  }

  /**
   * Close artifact type menu
   */
  function closeArtifactTypeMenu(): void {
    artifactTypeMenuState.value = undefined
  }

  /**
   * Set the linked artifact IDs for the current selection.
   * Used by HTML artifact row components to render the Linked visual state.
   */
  function setLinkedArtifactIds(ids: Set<number>): void {
    linkedArtifactIds.value = ids
  }

  /**
   * Clear the linked artifact IDs (on deselect / reset)
   */
  function clearLinkedArtifactIds(): void {
    linkedArtifactIds.value = new Set()
  }

  /**
   * Open linked types popup (shown when clicking on dot area).
   * Suppresses the next closeLinkedTypesPopup call so that the v-overlay
   * click:outside event (which fires synchronously on the same click) cannot
   * immediately close a popup that was just opened.
   */
  function openLinkedTypesPopup(
    artifact: Artifact,
    direction: 'incoming' | 'outgoing',
    linkedTypes: LinkedArtifactTypeInfo[],
    position: { x: number; y: number },
  ): void {
    linkedTypesPopupState.value = { artifact, direction, linkedTypes, position }
  }

  /**
   * Close linked types popup
   */
  function closeLinkedTypesPopup(): void {
    linkedTypesPopupState.value = undefined
  }

  /**
   * Trigger artifact click from HTML artifact list
   * Replaces direct JointJS cell click handling for scrollable containers
   */
  function triggerArtifactClick(artifact: Artifact): void {
    artifactClickTrigger.value = { artifact, timestamp: Date.now() }
  }

  /**
   * Trigger artifact double-click from HTML artifact list
   * Used to fetch additional linked data (expand trace graph)
   */
  function triggerArtifactDblClick(artifact: Artifact): void {
    artifactDblClickTrigger.value = { artifact, timestamp: Date.now() }
  }

  /**
   * Trigger loading more artifacts for an artifact type
   * Used by scrollable container infinite scroll
   */
  function triggerLoadMoreArtifacts(artifactType: ArtifactType): void {
    loadMoreArtifactsTrigger.value = { artifactType, timestamp: Date.now() }
  }

  /**
   * Set loaded artifacts list
   */
  function setLoadedArtifacts(artifacts: Artifact[]): void {
    loadedArtifacts.value = artifacts
  }

  function toggleArtifactTypeCollapsed(typeId: number, collapsed: boolean): void {
    const state = artifactTypeUiStates.value.get(typeId)
    if (state) {
      state.collapsed = collapsed
    } else {
      artifactTypeUiStates.value.set(typeId, { collapsed, maxArtifactCountToShow: 0 })
    }
  }

  function setMaxArtifactCountToShow(typeId: number, maxCount: number): void {
    const state = artifactTypeUiStates.value.get(typeId)
    if (state) {
      state.maxArtifactCountToShow = maxCount
    } else {
      artifactTypeUiStates.value.set(typeId, { collapsed: true, maxArtifactCountToShow: maxCount })
    }
  }

  function setContainerSearchArtifactCount(typeId: number, count: number): void {
    const state = artifactTypeUiStates.value.get(typeId)
    if (state) {
      state.containerSearchArtifactCount = count
    } else {
      artifactTypeUiStates.value.set(typeId, {
        collapsed: true,
        maxArtifactCountToShow: 0,
        containerSearchArtifactCount: count,
      })
    }
  }

  function expandArtifactTypesForLoadedArtifacts(artifacts: Artifact[]): void {
    const typeIdCounts = new Map<number, number>()
    artifacts.forEach((artifact) => {
      const typeId = artifact.artifactType.id
      typeIdCounts.set(typeId, (typeIdCounts.get(typeId) || 0) + 1)
    })
    typeIdCounts.forEach((count, typeId) => {
      const state = artifactTypeUiStates.value.get(typeId)
      if (state) {
        state.collapsed = false
        state.maxArtifactCountToShow = count
      } else {
        artifactTypeUiStates.value.set(typeId, { collapsed: false, maxArtifactCountToShow: count })
      }
    })
  }

  function initializeArtifactTypeUiStates(artifactTypes: ArtifactType[]): void {
    artifactTypes.forEach((type) => {
      if (!artifactTypeUiStates.value.has(type.id)) {
        artifactTypeUiStates.value.set(type.id, { collapsed: true, maxArtifactCountToShow: 0 })
      }
    })
  }

  function getArtifactTypeUiState(typeId: number): ArtifactTypeUiState | undefined {
    return artifactTypeUiStates.value.get(typeId)
  }

  function clearArtifactTypeUiStates(): void {
    artifactTypeUiStates.value.clear()
  }

  function clearSelection(): void {
    selectedArtifactId.value = undefined
    selectedArtifactTypeId.value = undefined
  }

  /**
   * Reset all UI state
   */
  function resetUiState(): void {
    selectedArtifactId.value = undefined
    selectedArtifactTypeId.value = undefined
    selectedSnapshotId.value = undefined
    fixedArtifactId.value = null
    highlightAllDirectLinks.value = false
    searchMode.value = false
    loadedArtifacts.value = []
    propertiesPanelArtifact.value = undefined
    propertiesPanelArtifactType.value = undefined
    searchResults.value = []
    highlightTrigger.value = undefined
    artifactTypeMenuState.value = undefined
    linkedArtifactIds.value = new Set()
    linkedTypesPopupState.value = undefined
    artifactTypeUiStates.value.clear()
  }

  const selectedArtifact = computed(() => {
    const id = selectedArtifactId.value
    return id ? useArtifactCacheStore().getArtifactById(parseInt(id)) : undefined
  })

  const selectedArtifactType = computed(() => {
    const id = selectedArtifactTypeId.value
    return id ? useArtifactCacheStore().getArtifactType(id) : undefined
  })

  // Return public API
  return {
    // State
    selectedArtifactId,
    selectedArtifactTypeId,
    selectedSnapshotId,
    fixedArtifactId,
    highlightAllDirectLinks,
    searchMode,
    loadedArtifacts,
    propertiesPanelArtifact,
    propertiesPanelArtifactType,
    artifactTypeUiStates,
    selectedArtifact,
    selectedArtifactType,
    searchResults,
    artifactTypeSearchTrigger,
    highlightTrigger,
    artifactTypeMenuState,
    linkedArtifactIds,
    linkedTypesPopupState,
    artifactClickTrigger,
    artifactDblClickTrigger,
    loadMoreArtifactsTrigger,
    // Actions
    setSelectedArtifact,
    setSelectedArtifactType,
    setSelectedSnapshot,
    toggleFixedArtifact,
    clearFixedArtifact,
    isArtifactFixed,
    toggleHighlightAllDirectLinks,
    setHighlightAllDirectLinks,
    setSearchMode,
    showArtifactProperties,
    showArtifactTypeProperties,
    setSearchResults,
    clearSearchResults,
    triggerArtifactTypeSearch,
    triggerHighlightForElement,
    openArtifactTypeMenu,
    closeArtifactTypeMenu,
    setLinkedArtifactIds,
    clearLinkedArtifactIds,
    openLinkedTypesPopup,
    closeLinkedTypesPopup,
    triggerArtifactClick,
    triggerArtifactDblClick,
    triggerLoadMoreArtifacts,
    setLoadedArtifacts,
    toggleArtifactTypeCollapsed,
    setMaxArtifactCountToShow,
    setContainerSearchArtifactCount,
    expandArtifactTypesForLoadedArtifacts,
    initializeArtifactTypeUiStates,
    getArtifactTypeUiState,
    clearArtifactTypeUiStates,
    clearSelection,
    resetUiState,
  }
})
